/**
 * Landing page motion.
 *
 * The page loads like a comic page being slapped down: panels drop in with a
 * short overshoot, the title slams letter by letter, and the props pop in
 * behind. Scrolling down reveals the remaining panels.
 *
 * Everything animates *from* a hidden state so that if the script never runs
 * (JS disabled, an error before this hook) the content is simply visible.
 */

import { useEffect, useRef, type RefObject } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

gsap.registerPlugin(useGSAP)

export function useLandingMotion(root: RefObject<HTMLElement>) {
  // Scroll reveals are driven by an observer rather than ScrollTrigger: the
  // page scrolls in its own container on some layouts, and an observer works
  // the same either way.
  const observerRef = useRef<IntersectionObserver | null>(null)

  useGSAP(
    () => {
      const el = root.current
      if (!el) return

      const mm = gsap.matchMedia()

      mm.add(
        {
          motionOk: '(prefers-reduced-motion: no-preference)',
          reduceMotion: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          const conditions = (context.conditions ?? {}) as Record<string, boolean>
          // Both branches are listed so the handler always runs; when motion is
          // reduced we simply skip the animation and leave the final state.
          if (conditions.reduceMotion === true) return

          const q = <T extends Element>(selector: string) =>
            Array.from(el.querySelectorAll<T>(selector))

          const timeline = gsap.timeline({ defaults: { ease: 'back.out(1.6)' } })

          timeline
            .from(q('[data-lp-nav]'), {
              yPercent: -110,
              autoAlpha: 0,
              duration: 0.5,
              ease: 'power3.out',
            })
            .from(
              q('[data-lp-hero-panel]'),
              { y: 52, rotation: 1.8, autoAlpha: 0, duration: 0.8 },
              0.08,
            )
            .from(
              q('[data-lp-figure]'),
              { y: 70, rotation: -4, scale: 0.93, autoAlpha: 0, duration: 0.72, stagger: 0.12 },
              0.22,
            )
            .from(
              q('[data-lp-letter]'),
              {
                y: -52,
                rotation: -9,
                autoAlpha: 0,
                duration: 0.44,
                stagger: 0.05,
                ease: 'back.out(2.6)',
              },
              0.34,
            )
            .from(
              q('[data-lp-copy]'),
              { y: 22, autoAlpha: 0, duration: 0.52, stagger: 0.08, ease: 'power2.out' },
              0.3,
            )
            .from(
              q('[data-lp-burst]'),
              { scale: 0.25, autoAlpha: 0, duration: 0.62, ease: 'back.out(3)' },
              0.78,
            )
            .from(
              q('[data-lp-sticker]'),
              { scale: 0.45, rotation: -28, autoAlpha: 0, duration: 0.58, ease: 'back.out(2.8)' },
              0.9,
            )

          // Ambient: the burst keeps breathing so the hero is never static.
          gsap.to('[data-lp-burst]', {
            scale: 1.07,
            duration: 1.7,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: 1.6,
          })

          // Scroll reveals. The panels are never hidden — they are simply
          // below the fold when the observer fires, so sliding them up reads
          // as rising into view. Nothing can be left stranded invisible, and
          // a static capture of the whole page still shows every panel.
          const reveal = q<HTMLElement>('[data-lp-reveal]')
          if (reveal.length) {
            const observer = new IntersectionObserver(
              (entries) => {
                for (const entry of entries) {
                  if (!entry.isIntersecting) continue
                  observer.unobserve(entry.target)
                  gsap.from(entry.target, {
                    y: 46,
                    duration: 0.7,
                    ease: 'power2.out',
                  })
                }
              },
              { threshold: 0.05, rootMargin: '0px 0px -8% 0px' },
            )
            reveal.forEach((node) => observer.observe(node))
            observerRef.current = observer
          }
        },
      )

      // matchMedia owns its own context; useGSAP only reverts its own.
      return () => {
        mm.revert()
      }
    },
    { dependencies: [] },
  )

  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [],
  )
}
